import {
  AbstractControl,
  FormControl,
  FormControlStatus,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn
} from '@angular/forms';
import { isObject, removeByIndex, rightPad, strongValue } from '@mng/mng-ts-helpers';
import { Subscription } from 'rxjs';
import { findOption, getRealOptionLabel, IOption } from './input.types';
import { Injector, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IMessageResolvers } from './fields/message.helper';

export interface IHasId {
  id?: number;
}

/**
 * Basis Abstraktionen für die Arbeit mit FormControl, FormGroup, FormArray
 */

export type UiItemFunction = (uiGroup?: UiGroup<any>, item?: BaseUiItem<any>) => boolean;

export const markAsTouchedFunc = (item: UntypedFormControl) => {
  item.markAsTouched();
};

export const markAsTouchedAndUpdateValueAndValidityFunc = (item: UntypedFormControl) => {
  item.markAsTouched();
  item.updateValueAndValidity();
};

export const markAsPristineAndUpdateValueAndValidityFunc = (item: UntypedFormControl) => {
  item.markAsPristine();
  item.updateValueAndValidity();
};

export const ONLY_SELF_AND_NO_EMIT = { onlySelf: true, emitEvent: false };

export const setDisabledFunc = (item: UntypedFormControl) => item.disable(ONLY_SELF_AND_NO_EMIT);
export const setEnabledFunc = (item: UntypedFormControl) => item.enable(ONLY_SELF_AND_NO_EMIT);

export class WithSubscriptions {
  subscriptions: Subscription[] = [];

  addSubscription(subscription: Subscription) {
    this.subscriptions.push(subscription);
  }

  destroySubscriptions() {
    for (const subscritpion of this.subscriptions) {
      subscritpion.unsubscribe();
    }
    this.subscriptions = [];
  }
}

export class BaseUiItem<T extends AbstractControl, ObjectType = any> extends WithSubscriptions {
  control: T;

  disabledFunction: UiItemFunction;

  signal: Signal<ObjectType>;

  constructor(
    control: T,
    public injector?: Injector
  ) {
    super();
    this.control = control;
  }

  static executeFunctionForFormGroup(formGroup: UntypedFormGroup, func: (item: UntypedFormControl) => void): void {
    Object.values(formGroup.controls).forEach((control: AbstractControl) =>
      this.executeFunctionForControl(control, func)
    );
  }

  static executeFunctionForControl(control: AbstractControl, func: (item: UntypedFormControl) => void) {
    if (control instanceof UntypedFormControl) {
      func(control);
    } else if (control instanceof UntypedFormGroup) {
      this.executeFunctionForFormGroup(control, func);
    } else if (control instanceof UntypedFormArray) {
      this.executeFunctionForFormArray(control, func);
    }
  }

  static executeFunctionForFormArray(formArray: UntypedFormArray, func: (item: UntypedFormControl) => void): void {
    formArray.controls.forEach((control: AbstractControl) => this.executeFunctionForControl(control, func));
  }

  disable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): this {
    this.control.disable(opts);
    return this;
  }

  enable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): this {
    this.control.enable(opts);
    return this;
  }

  // enable handling mit defaults von angular
  enableHandlingDefault(enabled: boolean) {
    this.enableHandling(enabled, { onlySelf: false, emitEvent: true });
  }

  enableHandling(enabled: boolean, opts = { onlySelf: true, emitEvent: true }) {
    if (enabled && !this.control.enabled) {
      this.control.enable(opts);
    } else if (!enabled && !this.control.disabled) {
      this.control.disable(opts);
    }
  }

  cleanup(): void {
    this.destroySubscriptions();
  }

  isField(): boolean {
    return this.control instanceof UntypedFormControl;
  }

  asSignal(): Signal<ObjectType> {
    if (!this.signal) {
      try {
        this.signal = toSignal(this.control.valueChanges, {
          injector: this.injector,
          initialValue: this.control.value
        });
      } catch (e) {
        console.error(e);
        throw new Error('Ein möglicher Grund für den Fehler ist das keine injector gesetzt ist!');
      }
    }
    return this.signal;
  }

  updateValueAndValidityRecursive() {
    BaseUiItem.executeFunctionForControl(this.control, (control: UntypedFormControl) =>
      control.updateValueAndValidity()
    );
  }
}

// ----------------------------------------------------------------
// UiField

export class UiField<ObjectType = any> extends BaseUiItem<FormControl<ObjectType>> {
  messageResolvers: IMessageResolvers;
  #options: IOption[];
  #optionsSignal: Signal<IOption[]>;
  placeHolder?: string;

  constructor(
    public id: string,
    formState?: ObjectType,
    public label = '',
    injector?: Injector
  ) {
    super(new FormControl<ObjectType>(formState), injector);
  }

  get value(): ObjectType | undefined {
    return this.control?.value;
  }

  get options(): IOption[] | undefined {
    return this.#optionsSignal ? this.#optionsSignal() : this.#options;
  }

  withLabel(value: string): this {
    this.label = value;
    return this;
  }

  withPlaceholer(value: string): this {
    this.placeHolder = value;
    return this;
  }

  withMessageResolvers(value: IMessageResolvers): this {
    this.messageResolvers = value;
    return this;
  }

  withValidators(...newValidator: ValidatorFn[] | null): this {
    this.control.setValidators(newValidator);
    return this;
  }

  withOptions(options: IOption[]): this {
    this.#options = options;
    return this;
  }

  withOptionsSignal(optionsSignal: Signal<IOption[]>): this {
    this.#optionsSignal = optionsSignal;
    return this;
  }

  withDisabled(func: UiItemFunction): this {
    this.disabledFunction = func;
    return this;
  }

  setControlValue(value: ObjectType) {
    this.control.setValue(value, ONLY_SELF_AND_NO_EMIT);
  }

  setValueAndTouchedAndDirty(value: ObjectType) {
    this.control.setValue(value);
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  reset(value?: any) {
    this.control.reset(value);
  }

  info(html = true): string | undefined {
    if (!this.control.value) {
      return undefined;
    }

    let infoValue: string;

    if (this.options) {
      const option = findOption(this.options, this.control.value);
      if (option) {
        infoValue = getRealOptionLabel(option);
      }
    } else {
      infoValue = JSON.stringify(this.control.value);
    }

    const label = this.label ? `${this.label}: ` : '';

    return `${label}${strongValue(infoValue, html)}`;
  }
  selectedOptionsLabel(): string | undefined {
    if (!this.options) {
      console.error('Für das Feld ' + this.id + ' sind keine Options definiert');
      return;
    }

    if (!this.control.value) {
      return;
    }

    const option = this.options.find((current) => current.value === this.control.value);
    if (option) {
      return getRealOptionLabel(option);
    }
  }

  /**
   * Überprüfen ob der control value tatsächlich auch als "value" im options array enthalten ist
   */
  isOptionValueSelected(): boolean {
    const currentValue = this.control.value;
    return this.options?.some((option) => option.value === currentValue);
  }

  hasValidOptionValue(): boolean {
    return !!this.control.value && this.isOptionValueSelected();
  }

  logValidStatus(statusFilter?: FormControlStatus) {
    if (!statusFilter || this.control.status === statusFilter) {
      console.log(
        `${rightPad(this.id, 30, ' ')}`,
        'Status:',
        rightPad(this.control.status, 12, ' '),
        'Value:',
        this.control.value
      );
    }
  }
}

/**
 * Uses value object from options (IOption[]) list to set field controls value
 */
export function setValueAsOptionsValueObject<T extends IHasId>(field: UiField, value: T | undefined): UiField {
  if (field.options && field.options.length > 0) {
    const foundOption = field.options.find((option) => {
      if (!option.value && !value) {
        return true;
      } else if (value && option.value && typeof option.value === 'object' && 'id' in option.value) {
        const result = option.value.id === value.id;
        return result;
      } else {
        return false;
      }
    });
    if (foundOption) {
      field.control.setValue(foundOption.value);
    }
  } else {
    console.error('Field ' + field.id + ' doest not have options');
  }
  return field;
}

// ----------------------------------------------------------------
// UiGroup

export class UiGroup<ObjectType> extends BaseUiItem<UntypedFormGroup, ObjectType> {
  valueChangeSubscription: Subscription;

  readonly items: { [key: string]: BaseUiItem<any> } = {};

  readonly #dataAsSignal = signal<ObjectType>(undefined);
  readonly dataAsSignal = this.#dataAsSignal.asReadonly();

  constructor(data?: ObjectType, injector?: Injector) {
    super(new UntypedFormGroup({}), injector);
    this.#dataAsSignal.set(data);
  }

  get data(): ObjectType {
    return this.#dataAsSignal();
  }

  init() {
    this.handleEnablingOrDisablingOfFields();
    if (this.itemsForEnabledOrDisabledHandling().length > 0) {
      this.startDynamicEnablingOrDisablingOfFields();
    }
  }

  setData(data: ObjectType) {
    this.#dataAsSignal.set(data);
  }

  getUpdatedData(): ObjectType {
    let result = this.getRawValue();

    if (this.data) {
      result = { ...this.data, ...result };
    }

    if (this.items) {
      Object.keys(this.items).forEach((itemKey) => {
        const item = this.items[itemKey];
        if (item instanceof UiGroup) {
          result[itemKey] = item.getUpdatedData();
        } else if (item instanceof UiArray && item.items?.length > 0 && item.items[0] instanceof UiGroup) {
          // Array Behandlung mit UiGroup Items
          result[itemKey] = item.items.map((arrayItem) => arrayItem.getUpdatedData());
        }
      });
    }

    return result;
  }

  // addField<K extends keyof ObjectType, T = any>(id: K, formState?: T, label = ''): UiField<T> {
  addField<K extends keyof ObjectType, T = any>(id: K, formState?: T, label = ''): UiField<T> {
    const field = new UiField<T>(id.toString(), formState, label, this.injector);
    this.add(id, field);
    return field;
  }

  addFields<K extends keyof ObjectType>(...fields: UiField[]): UiGroup<ObjectType> {
    fields.forEach((field) => this.add(field.id as K, field));
    return this;
  }

  addFieldWithOptions<K extends keyof ObjectType, T = IHasId>(
    id: K,
    formState?: T,
    label = '',
    options: IOption[] = []
  ): UiField<T> {
    const field = this.addField(id, formState, label).withOptions(options);

    if (options?.length > 0 && isObject(options[0].value)) {
      setValueAsOptionsValueObject(field, formState);
    }
    return field;
  }

  field<K extends keyof ObjectType>(id: K): UiField<any> {
    const item = this.items[id.toString()];
    if (!item) {
      throw new Error(`Field with id ${id.toString()} not found`);
    }
    if (!item?.isField()) {
      throw new Error(`Field with id ${id.toString()} is not a field`);
    }
    return item as UiField;
  }

  addGroup<K extends keyof ObjectType>(id: K, group: UiGroup<any>): UiGroup<ObjectType> {
    this.add(id, group);
    return group;
  }

  add<K extends keyof ObjectType>(id: K, item: BaseUiItem<any>) {
    this.control.addControl(id.toString(), item.control);
    this.items[id.toString()] = item;
    return item;
  }

  addArray(id: string, uiArray: UiArray<any>): this {
    this.control.addControl(id, uiArray.formArray);
    this.items[id] = uiArray;
    return this;
  }

  getRawValue() {
    return this.control.getRawValue();
  }

  update(value: any) {
    this.control.patchValue(value || {}, ONLY_SELF_AND_NO_EMIT);
  }

  reset(value: any) {
    this.control.reset(value);
  }

  isValid(): boolean {
    return this.control.valid;
  }

  markAsPristine() {
    this.control.markAsPristine({ onlySelf: false });
  }

  markAllFieldsAsTouched() {
    BaseUiItem.executeFunctionForFormGroup(this.control, markAsTouchedFunc);
  }

  markAllFieldsAsTouchedAndUpdateValueAndValidity() {
    BaseUiItem.executeFunctionForFormGroup(this.control, markAsTouchedAndUpdateValueAndValidityFunc);
  }

  markAllFieldsAsPristineAndUpdateValueAndValidity() {
    BaseUiItem.executeFunctionForFormGroup(this.control, markAsPristineAndUpdateValueAndValidityFunc);
  }

  setAllFieldsDisabled() {
    BaseUiItem.executeFunctionForFormGroup(this.control, setDisabledFunc);
  }

  setAllFieldsEnabled() {
    BaseUiItem.executeFunctionForFormGroup(this.control, setEnabledFunc);
  }

  itemsForEnabledOrDisabledHandling(): BaseUiItem<any>[] {
    return Object.values(this.items).filter((item) => item.disabledFunction);
  }

  handleEnablingOrDisablingOfFields() {
    this.itemsForEnabledOrDisabledHandling().forEach((item) => {
      const enabled = !item.disabledFunction(this, item);
      this.enableHandling(enabled);
    });
  }

  cleanup(): void {
    super.cleanup();
    this.stopDynamicEnablingOrDisablingOfFields();
  }

  /**
   * ACHTUNG- jede Subscription muss auch wieder aufgeräumt werden -> unsubscribe via "cleanup"
   * -> z.B. wenn die Componnent "destroyed" wird
   */
  startDynamicEnablingOrDisablingOfFields(): void {
    this.valueChangeSubscription = this.control.valueChanges.subscribe((data) => {
      this.handleEnablingOrDisablingOfFields();
    });
  }

  stopDynamicEnablingOrDisablingOfFields(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  info(): string {
    const fieldInfos = [];
    for (const field of Object.values(this.items)) {
      if (field instanceof UiField) {
        const fieldInfo = field.info();
        if (fieldInfo) {
          fieldInfos.push(fieldInfo);
        }
      }
    }
    return fieldInfos.join(', ');
  }

  logInvalidFieldsItemsStati() {
    this.logFieldsItemsStati('INVALID');
  }

  logFieldsItemsStati(statusFilter?: FormControlStatus) {
    this.#logFieldsItemsStatiForGroup(this, statusFilter);
  }

  #logFieldsItemsStatiForGroup(uiGroup: UiGroup<any>, statusFilter?: FormControlStatus) {
    Object.keys(uiGroup.items).forEach((key: string) => {
      const item: BaseUiItem<any> = uiGroup.items[key];
      if (item instanceof UiGroup) {
        this.#logFieldsItemsStatiForGroup(item);
      } else if (item instanceof UiArray) {
        item.logFieldsItemsStati(statusFilter);
      } else if (item instanceof UiField) {
        item.logValidStatus(statusFilter);
      }
    });
  }
}

// ----------------------------------------------------------------
// UiArray

export class UiArray<T extends UiGroup<unknown> | UiField> extends BaseUiItem<UntypedFormArray> {
  items: T[] = [];

  constructor(injector?: Injector) {
    super(new UntypedFormArray([]), injector);
  }

  add(...items: T[]): UiArray<T> {
    this.items = [...this.items, ...items];
    items.forEach((item) => this.control.push(item.control));

    return this;
  }

  get formArray(): UntypedFormArray {
    return this.control;
  }

  addAndMarkDirtyAndTouched(...items: T[]): UiArray<T> {
    this.addAndMarkDirty(...items);
    this.formArray.markAllAsTouched();
    return this;
  }

  addAndMarkDirty(...items: T[]): UiArray<T> {
    this.add(...items);

    items.forEach((item) => {
      item.control.markAsDirty();
    });
    this.formArray.markAsDirty();
    return this;
  }

  removeByIndex(index: number) {
    this.items = removeByIndex(this.items, index);
    this.formArray.removeAt(index);
    this.formArray.markAsDirty();
  }

  markAllFieldsAsTouched() {
    BaseUiItem.executeFunctionForFormArray(this.formArray, markAsTouchedAndUpdateValueAndValidityFunc);
  }

  updateItemAt(index: number, item: T) {
    this.items[index] = item;
    this.formArray.setControl(index, item.control);
  }

  destroySubscriptions() {
    super.destroySubscriptions();
    for (const item of this.items) {
      item.destroySubscriptions();
    }
  }

  getUpdatedData() {
    if (this.items.length > 0 && this.items[0] instanceof UiGroup) {
      return this.items.map((item) => (item as UiGroup<unknown>).getUpdatedData());
    } else {
      return this.control.getRawValue();
    }
  }

  logFieldsItemsStati(statusFilter?: FormControlStatus) {
    this.items.forEach((arrayItem: UiField | UiGroup<any>, index) => {
      if (arrayItem instanceof UiField) {
        arrayItem.logValidStatus(statusFilter);
      } else if (arrayItem instanceof UiGroup) {
        arrayItem.logFieldsItemsStati(statusFilter);
      }
    });
  }
}
